import { ExternalLink } from "lucide-react";
import { formatDate, formatRelative } from "@/lib/utils";
import type { PortalDealDetail } from "@colab/shared";
import { DealStatusBadge } from "./deal-status-badge";
import { AttributionDirectionBadge } from "./attribution-direction-badge";
import { HubspotOwnerChip } from "./hubspot-owner-chip";
import { DealCommentThread } from "./deal-comment-thread";
import { DealDecisionTimeline } from "./deal-decision-timeline";
import { formatCurrency } from "./format";

// Phase 7 sub-bucket 5 — read-only deal detail view. Shown when the deal is
// NOT in the editable subset (i.e. NOT source='deal' + status='submitted' +
// partner-submitted + not withdrawn). Includes:
//   - Header with status + attribution badges + amount
//   - Deal facts panel (read-only)
//   - HubSpot owner chip (only when set)
//   - Comment thread (only for source='deal' — attribution-only deals
//     use HubSpot as the conversational surface)
//   - Status timeline (only for source='deal'; attribution rows have no
//     internal status_history)

export function DealReadOnlyView({ detail }: { detail: PortalDealDetail }) {
  const isDealRow = detail.source === "deal";
  return (
    <div>
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {isDealRow ? "Submitted lead" : "Attributed in HubSpot"}
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            {detail.customer_name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <DealStatusBadge row={detail} />
            <AttributionDirectionBadge row={detail} />
          </div>
        </div>
        <div className="text-right">
          <p className="font-display text-3xl font-semibold tabular-nums">
            {formatCurrency(detail.amount, detail.currency_code)}
          </p>
          {detail.close_date ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Expected close {formatDate(detail.close_date)}
            </p>
          ) : null}
          {detail.updated_at ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Updated {formatRelative(detail.updated_at)}
            </p>
          ) : null}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-xl border bg-card p-5">
            <h3 className="text-sm font-medium text-foreground">Details</h3>
            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
              <Fact
                label="Customer"
                value={detail.customer_name}
              />
              {detail.prospect_contact_name ? (
                <Fact label="Contact" value={detail.prospect_contact_name} />
              ) : null}
              {detail.prospect_contact_email ? (
                <Fact label="Email" value={detail.prospect_contact_email} />
              ) : null}
              {detail.deal_type ? (
                <Fact label="Type" value={formatDealType(detail.deal_type)} />
              ) : null}
              {detail.stage_label ? (
                <Fact label="HubSpot stage" value={detail.stage_label} />
              ) : null}
              <Fact
                label="Attribution"
                value={attributionExplanation(detail)}
              />
            </dl>

            {detail.notes && !detail.is_withdrawn ? (
              <div className="mt-5">
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Notes
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                  {detail.notes}
                </p>
              </div>
            ) : null}

            {detail.hubspot_url ? (
              <div className="mt-5">
                <a
                  href={detail.hubspot_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
                >
                  Open in HubSpot
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : null}
          </section>

          {isDealRow ? (
            <section className="rounded-xl border bg-card p-5">
              <DealCommentThread dealId={detail.id} canPost={true} />
            </section>
          ) : null}
        </div>

        <aside className="space-y-6">
          {detail.hubspot_owner ? (
            <section className="rounded-xl border bg-card p-5">
              <h3 className="text-sm font-medium text-foreground">
                HubSpot owner
              </h3>
              <div className="mt-3">
                <HubspotOwnerChip owner={detail.hubspot_owner} />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Your Bookboost collaborator on this deal in HubSpot.
              </p>
            </section>
          ) : null}

          {isDealRow ? (
            <section className="rounded-xl border bg-card p-5">
              <DealDecisionTimeline
                dealId={detail.id}
                isWithdrawn={detail.is_withdrawn}
              />
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-foreground">{value}</dd>
    </div>
  );
}

function formatDealType(t: string): string {
  switch (t) {
    case "referral":
      return "Referral";
    case "co_sell":
      return "Co-sell";
    case "reseller":
      return "Reseller";
    default:
      return t;
  }
}

function attributionExplanation(detail: PortalDealDetail): string {
  if (detail.direction === "inbound") {
    return "You introduced this customer to Bookboost.";
  }
  if (detail.relationship === "referred_to_them") {
    return "Bookboost introduced this customer to you.";
  }
  if (detail.relationship === "influenced") {
    return "Co-influenced by you and Bookboost.";
  }
  return "Outbound flow.";
}
